import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Button,
  Alert,
  Badge,
  Collapse,
  Spinner,
} from 'react-bootstrap';
import { toast } from 'react-toastify';

import SubtleBadge from 'components/common/SubtleBadge';
import ConfirmModal from 'components/stocktaking/ConfirmModal';

import { stocktakingApi } from 'api';
import { useUserContext } from 'providers/UserProvider';
import paths from 'routes/paths';
import { TIME_OPTION } from 'constants/time';
import {
  STOCKTAKING_STATUS,
  STOCKTAKING_STATUS_LABEL,
  STOCKTAKING_SCOPE_LABEL,
  getStocktakingBadgeColor,
  getScopeBadgeColor,
} from 'constants/stocktaking/status';
import { getStocktakingLineColumns } from 'constants/stocktaking/columns';

import StocktakingLinesTable from './StocktakingLinesTable';

const StocktakingDetail = () => {
  const { tenant, warehouse, stocktakingId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useUserContext();
  const fileInputRef = useRef(null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [showSkuList, setShowSkuList] = useState(false);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    content: null,
    confirmText: '',
    confirmVariant: 'primary',
    onConfirm: () => {},
  });

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      const result = await stocktakingApi.getById(stocktakingId);
      setData(result);
    } catch {
      toast.error('Failed to load stocktaking details.');
    } finally {
      setLoading(false);
    }
  }, [stocktakingId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const lineColumns = useMemo(() => getStocktakingLineColumns(), []);

  const hasNullCounted = data?.lines?.some(l => l.counted_quantity === null);
  const hasOverResult = data?.lines?.some(l => l.over_result !== null);
  const hasRemainingExcess = data?.lines?.some(
    l => l.over_result?.remaining > 0,
  );
  const status = data?.status;

  // ─── Actions ───

  const handleDownload = async () => {
    try {
      setActionLoading(true);
      const blob = await stocktakingApi.download(stocktakingId);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data.stocktaking_code}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error.response?.status !== 429) {
        toast.error('Failed to download Excel.');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleExcessDownload = async () => {
    try {
      setActionLoading(true);
      const blob = await stocktakingApi.excessDownload(stocktakingId);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data.stocktaking_code}_excess.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error.response?.status !== 429) {
        toast.error('Failed to download excess report.');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setUploadErrors([]);
    setActionLoading(true);
    try {
      const result = await stocktakingApi.upload(stocktakingId, file);
      setData(result);
      toast.success('Upload & prediction completed.');
    } catch (error) {
      const errData = error?.response?.data;
      if (Array.isArray(errData)) {
        setUploadErrors(errData);
      } else if (errData && typeof errData === 'object') {
        setUploadErrors(Object.values(errData).flat());
      } else {
        toast.error('Failed to upload Excel.');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleApply = () => {
    setConfirmModal({
      show: true,
      title: 'Apply Inventory Adjustment (Phase 1)',
      content: (
        <>
          <p>
            Inventory quantities will be adjusted.{' '}
            <strong className="text-danger">
              This action cannot be undone.
            </strong>
          </p>
          {hasOverResult && (
            <Alert variant="info" className="mb-0">
              There are items requiring Phase 2 (reject). Additional processing
              will be needed after this step.
            </Alert>
          )}
        </>
      ),
      confirmText: 'Apply Adjustment',
      confirmVariant: 'danger',
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const result = await stocktakingApi.apply(stocktakingId);
          setData(result);
          toast.success('Inventory adjustment completed.');
        } catch (error) {
          const errData = error?.response?.data;
          const msg = Array.isArray(errData)
            ? errData.join(', ')
            : 'Failed to apply adjustment.';
          toast.error(msg);
        } finally {
          setActionLoading(false);
          setConfirmModal(prev => ({ ...prev, show: false }));
        }
      },
    });
  };

  const handleReject = () => {
    setConfirmModal({
      show: true,
      title: 'Auto Reject (Phase 2)',
      content: (
        <p>
          CREATED orders with excess quantities will be automatically rejected.
          Do you want to continue?
        </p>
      ),
      confirmText: 'Execute Reject',
      confirmVariant: 'warning',
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const result = await stocktakingApi.reject(stocktakingId);
          setData(result);
          const remaining = result.lines?.some(
            l => l.over_result?.remaining > 0,
          );
          if (remaining) {
            toast.warning(
              'Some items could not be fully rejected. Download excess report for details.',
            );
          } else {
            toast.success('Auto reject completed.');
          }
        } catch (error) {
          const errData = error?.response?.data;
          const msg = Array.isArray(errData)
            ? errData.join(', ')
            : 'Failed to execute reject.';
          toast.error(msg);
        } finally {
          setActionLoading(false);
          setConfirmModal(prev => ({ ...prev, show: false }));
        }
      },
    });
  };

  const handleComplete = () => {
    setConfirmModal({
      show: true,
      title: 'Complete Stocktaking',
      content: (
        <>
          <p>Do you want to complete this stocktaking?</p>
          {hasRemainingExcess && (
            <Alert variant="warning" className="mb-0">
              There are unprocessed excess items. Manual handling will be
              required after completion.
            </Alert>
          )}
        </>
      ),
      confirmText: 'Complete',
      confirmVariant: 'success',
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const result = await stocktakingApi.complete(stocktakingId);
          setData(result);
          toast.success('Stocktaking completed.');
        } catch (error) {
          const errData = error?.response?.data;
          const msg = Array.isArray(errData)
            ? errData.join(', ')
            : 'Failed to complete stocktaking.';
          toast.error(msg);
        } finally {
          setActionLoading(false);
          setConfirmModal(prev => ({ ...prev, show: false }));
        }
      },
    });
  };

  const handleAbort = () => {
    setConfirmModal({
      show: true,
      title: 'Abort Stocktaking',
      content: (
        <p>
          Do you want to abort this stocktaking? No inventory changes will be
          made.
        </p>
      ),
      confirmText: 'Abort',
      confirmVariant: 'secondary',
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const result = await stocktakingApi.abort(stocktakingId);
          setData(result);
          toast.success('Stocktaking aborted.');
        } catch (error) {
          const errData = error?.response?.data;
          const msg = Array.isArray(errData)
            ? errData.join(', ')
            : 'Failed to abort stocktaking.';
          toast.error(msg);
        } finally {
          setActionLoading(false);
          setConfirmModal(prev => ({ ...prev, show: false }));
        }
      },
    });
  };

  // ─── Render ───

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!data) {
    return <Alert variant="danger">Failed to load stocktaking details.</Alert>;
  }

  return (
    <>
      {/* Header */}
      <Card className="mb-3">
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0">
                {data.stocktaking_code}{' '}
                <SubtleBadge pill bg={getStocktakingBadgeColor(status)}>
                  {STOCKTAKING_STATUS_LABEL[status]}
                </SubtleBadge>
              </h5>
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() =>
                  navigate(paths.stocktakings.list(tenant, warehouse))
                }
              >
                Back to List
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <div className="mb-2">
                <strong>Scope: </strong>
                <SubtleBadge pill bg={getScopeBadgeColor(data.scope)}>
                  {STOCKTAKING_SCOPE_LABEL[data.scope]}
                </SubtleBadge>
                {data.scope === 'partial' && data.sku_list && (
                  <>
                    <Button
                      variant="link"
                      size="sm"
                      className="ms-2 p-0"
                      onClick={() => setShowSkuList(!showSkuList)}
                    >
                      SKU List {showSkuList ? 'Hide' : 'Show'} (
                      {data.sku_list.length})
                    </Button>
                    <Collapse in={showSkuList}>
                      <div className="mt-1">
                        {data.sku_list.map(sku => (
                          <Badge
                            key={sku}
                            bg="light"
                            text="dark"
                            className="me-1 mb-1"
                          >
                            {sku}
                          </Badge>
                        ))}
                      </div>
                    </Collapse>
                  </>
                )}
              </div>
              <div className="mb-2">
                <strong>Created By: </strong>
                {data.created_by?.email || '-'}
              </div>
              <div className="mb-2">
                <strong>Created: </strong>
                {new Date(data.created).toLocaleString('en-IN', TIME_OPTION)}
              </div>
            </Col>
            <Col md={6}>
              {data.completed_by && (
                <div className="mb-2">
                  <strong>Completed By: </strong>
                  {data.completed_by.email}
                </div>
              )}
              {data.completed_at && (
                <div className="mb-2">
                  <strong>Completed At: </strong>
                  {new Date(data.completed_at).toLocaleString(
                    'en-IN',
                    TIME_OPTION,
                  )}
                </div>
              )}
              <div className="mb-2">
                <strong>Upload Count: </strong>
                {data.upload_count}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Action Buttons */}
      <Card className="mb-3">
        <Card.Body className="d-flex flex-wrap gap-2 align-items-center">
          {status === STOCKTAKING_STATUS.IN_PROGRESS && (
            <>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleDownload}
                disabled={actionLoading}
              >
                Download Excel
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={actionLoading}
              >
                Upload Excel
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                className="d-none"
                onChange={handleUpload}
              />
              <Button
                variant="danger"
                size="sm"
                onClick={handleApply}
                disabled={actionLoading || hasNullCounted}
                title={hasNullCounted ? 'Please upload Excel first' : ''}
              >
                Apply Adjustment
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleAbort}
                disabled={actionLoading}
              >
                Abort
              </Button>
              {hasNullCounted && (
                <span className="text-muted small">
                  * Please upload Excel first
                </span>
              )}
            </>
          )}

          {status === STOCKTAKING_STATUS.ADJUSTED && (
            <>
              {isAdmin && !data.is_auto_rejected && (
                <Button
                  variant="warning"
                  size="sm"
                  onClick={handleReject}
                  disabled={actionLoading || !hasOverResult}
                  title={!hasOverResult ? 'No items to reject' : ''}
                >
                  Auto Reject (Phase 2)
                </Button>
              )}
              {isAdmin && (
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleComplete}
                  disabled={actionLoading}
                >
                  Complete
                </Button>
              )}
              {data.is_auto_rejected && hasRemainingExcess && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleExcessDownload}
                  disabled={actionLoading}
                >
                  Download Excess Excel
                </Button>
              )}
              {!isAdmin && (
                <span className="text-muted small">
                  * Reject and Complete require Admin permission
                </span>
              )}
            </>
          )}

          {(status === STOCKTAKING_STATUS.COMPLETED ||
            status === STOCKTAKING_STATUS.ABORTED) && (
            <span className="text-muted">Read only</span>
          )}
        </Card.Body>
      </Card>

      {/* Upload Errors */}
      {uploadErrors.length > 0 && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setUploadErrors([])}
          className="mb-3"
        >
          <Alert.Heading className="h6">Upload Errors</Alert.Heading>
          {uploadErrors.map((err, i) => (
            <div key={i}>{err}</div>
          ))}
        </Alert>
      )}

      {/* Lines Table */}
      <Card>
        <Card.Header>
          <h6 className="mb-0">
            Stocktaking Lines ({data.lines?.length || 0})
          </h6>
        </Card.Header>
        <Card.Body className="p-0">
          <StocktakingLinesTable
            lines={data.lines || []}
            columns={lineColumns}
          />
        </Card.Body>
      </Card>

      {/* Confirm Modal */}
      <ConfirmModal
        show={confirmModal.show}
        onHide={() => setConfirmModal(prev => ({ ...prev, show: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        confirmText={confirmModal.confirmText}
        confirmVariant={confirmModal.confirmVariant}
        loading={actionLoading}
      >
        {confirmModal.content}
      </ConfirmModal>
    </>
  );
};

export default StocktakingDetail;
